import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles/ChargerDropdown.module.scss';

import { EvChargingApp } from '../../omaLummeContext';

import { EmablerSiteUI, EmablerSocketUI } from '../../types/evCharging';
import { formatWatts } from '../../utils/evCharging';

type ChargerDropdownType = {
  site: EmablerSiteUI;
  allowEmpty?: boolean;
};

const ChargerDropdown: React.FunctionComponent<ChargerDropdownType> = (props: ChargerDropdownType) => {
  const { t } = useTranslation();
  const { selectedCharger } = EvChargingApp.useState();
  const dispatch = EvChargingApp.useDispatch();

  const { site, allowEmpty } = props;

  React.useEffect(() => {
    const charger = site.chargers.find((c) => c.chargerId === selectedCharger.split('-')[0]);
    if (!charger) {
      const firstChargerSocket = `${site.chargers[0].chargerId}-${site.chargers[0].sockets[0].id}`;
      dispatch(['changeSelectedCharger', firstChargerSocket]);
      window.localStorage.setItem('EvChargingChargerSocket', firstChargerSocket);
    }
  }, [site.chargers]);

  const [dropdownOpen, toggleDropdownOpen] = React.useState<boolean>(false);

  const escFunction = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        toggleDropdownOpen(false);
      }
    },
    [toggleDropdownOpen],
  );

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  function useOutsideAlerter(
    ref: React.RefObject<any>,
    open: {
      dropdownOpen: boolean;
      toggleDropdownOpen: (value: React.SetStateAction<boolean>) => void;
    },
  ) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target)) {
          open.toggleDropdownOpen(false);
        }
      }

      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, open]);
  }

  const chargerDropdownRef = useRef<any>(null);
  useOutsideAlerter(chargerDropdownRef, { dropdownOpen, toggleDropdownOpen });

  const availableChargers = site.chargers;

  const availableSockets = availableChargers
    .flatMap((charger) =>
      charger.sockets.map((socket) => ({
        chargerId: charger.chargerId,
        ...socket,
      })),
    )
    .sort((a, b) => a.chargerId.localeCompare(b.chargerId));

  const multipleChargerOptions = availableSockets.length > 1 || allowEmpty === true;

  const selectedChargerId = selectedCharger.split('-')[0];
  const selectedSocket = parseInt(selectedCharger.split('-')[1]);

  const activeSocket = availableSockets.find((s) => s.id === selectedSocket && s.chargerId === selectedChargerId);
  const activeCharger = availableChargers.find(
    (c) => c.chargerId === selectedCharger.split('-')[0] && c.sockets.find((s) => s.id === selectedSocket),
  );

  const socketStyle = (socket: EmablerSocketUI) => {
    let style = '';

    if (allowEmpty !== true) {
      if (socket.status === 'SuspendedCAR') {
        style = styles.suspended;
      }
      if (socket.status === 'Charging') {
        style = styles.charging;
      }
      if (socket.status === 'Preparing' || socket.status === 'Finishing') {
        style = styles.unplug;
      }
    }
    return style;
  };

  return (
    <div ref={chargerDropdownRef} className={`${styles.chargerDropdown} ${dropdownOpen ? styles.active : ''}`}>
      <button
        id="chargerDropdown"
        className={`${styles.activeCharger} ${
          multipleChargerOptions ? (dropdownOpen ? styles.chargerListOpen : styles.chargerListClosed) : styles.singleCharger
        }`}
        onClick={(): void => {
          if (document.getElementById('chargerDropdown')?.ariaDisabled === 'false') {
            toggleDropdownOpen(!dropdownOpen);
          }
        }}
        type="button"
        aria-disabled={!multipleChargerOptions}
      >
        <div className={`${styles.availableCharger} ${activeSocket ? socketStyle(activeSocket) : ''}`}>
          {selectedCharger === '' ? t(`evCharging:common.noChargerSelected`) : ''}
          {selectedCharger !== '' && (
            <>
              <span className={styles.activeChargerName}>{`${activeCharger?.chargerId.substring(activeCharger.chargerId.length - 9)}, ${t(
                `evCharging:common.socket`,
              )} ${activeSocket?.id.toString().substring(activeSocket?.id.toString().length - 1)}`}</span>
              <span className={styles.activeChargerId}>{`${formatWatts(activeSocket?.maxWatts ?? 0)}, ${activeSocket?.type}`}</span>
            </>
          )}
        </div>
      </button>
      {dropdownOpen && (
        <div className={styles.availableChargers}>
          {allowEmpty === true && (
            <button
              className={`${styles.availableCharger} ${selectedCharger === `` ? styles.activeCharger : ''}`}
              onClick={(): void => {
                dispatch(['changeSelectedCharger', ``]);
                toggleDropdownOpen(false);
              }}
            >
              <span className={styles.activeChargerName}>{t(`evCharging:common.noChargerSelected`)}</span>
            </button>
          )}
          {availableSockets.map((socket) => (
            <button
              className={`${styles.availableCharger} ${
                socket.chargerId === activeCharger?.chargerId && socket.id === activeSocket?.id ? styles.activeCharger : ''
              } ${socketStyle(socket)}`}
              key={`${socket.chargerId}-${socket.id}`}
              onClick={(): void => {
                const ChargerSocket = `${socket.chargerId}-${socket.id}`;
                dispatch(['changeSelectedCharger', ChargerSocket]);
                window.localStorage.setItem('EvChargingChargerSocket', ChargerSocket);
                toggleDropdownOpen(false);
              }}
            >
              <span className={styles.activeChargerName}>{`${socket.chargerId.substring(socket.chargerId.length - 9)}, ${t(
                `evCharging:common.socket`,
              )} ${socket?.id}`}</span>
              <span className={styles.activeChargerId}>{`${formatWatts(socket?.maxWatts ?? 0)}, ${socket?.type}`}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChargerDropdown;
